import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';

const StoryDetail = () => {
  const { storyId } = useParams();
  const navigate = useNavigate();
  const [story, setStory] = useState(null);

  // Blog stories data
  const stories = {
    'banarasi-weaving': {
      id: 'banarasi-weaving',
      title: 'The Art of Banarasi Weaving',
      date: 'January 15, 2026',
      author: 'Vastram Team',
      category: 'Heritage',
      image: 'https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=1200',
      excerpt: 'Discover the centuries-old tradition of Banarasi silk weaving and the artisans who keep it alive.',
      content: `
        <p>In the ancient city of Varanasi, where the Ganges flows and temple bells echo through narrow lanes, lives a tradition that has survived centuries—the art of Banarasi weaving.</p>
        
        <h2>A Legacy Woven in Gold</h2>
        <p>Banarasi sarees are not just garments; they are masterpieces that take weeks, sometimes months, to create. Each thread is carefully chosen, each pattern meticulously planned, and each motif carries centuries of cultural heritage.</p>
        
        <p>Master weavers like Ramesh Bhai, who learned the craft from his grandfather, spend hours at their looms creating intricate brocade patterns. "Every saree tells a story," he says, his fingers moving rhythmically across the warp and weft. "When a woman wears a Banarasi, she carries with her the dreams and dedication of generations of artisans."</p>
        
        <h2>The Making of Magic</h2>
        <p>The process begins with selecting the finest silk threads. Pure mulberry silk forms the base, while metallic threads—traditionally gold and silver—create the elaborate zari work that Banarasi sarees are famous for.</p>
        
        <p>Traditional motifs include kalga (paisley), bel (vine), and intricate floral patterns inspired by Mughal architecture. Each design is first drawn on graph paper, then translated into a language the loom can understand—a process that itself requires years of expertise.</p>
        
        <h2>Preserving an Art Form</h2>
        <p>At Vastram, we work directly with these master weavers, ensuring they receive fair compensation for their extraordinary skill. By choosing a Banarasi saree from us, you're not just buying silk and gold—you're supporting families, preserving heritage, and keeping an ancient art form alive for future generations.</p>
        
        <p>Every Banarasi saree in our collection comes with the story of its maker, connecting you directly to the hands that wove your dreams into reality.</p>
      `
    },
    'choosing-perfect-saree': {
      id: 'choosing-perfect-saree',
      title: 'Choosing the Perfect Saree',
      date: 'January 10, 2026',
      author: 'Priya Sharma',
      category: 'Style Guide',
      image: 'https://images.unsplash.com/photo-1739429945851-cf348b087e3d?w=1200',
      excerpt: 'A comprehensive guide to selecting the right saree for every occasion and body type.',
      content: `
        <p>Choosing the perfect saree can feel overwhelming with so many fabrics, colors, and styles available. But don't worry—we're here to guide you through the journey of finding your ideal drape.</p>
        
        <h2>Understanding Fabrics</h2>
        <p>The fabric you choose dramatically affects how a saree looks and feels:</p>
        
        <p><strong>Silk:</strong> Perfect for weddings and formal events. Heavy, luxurious, and makes a statement. Best for cooler weather.</p>
        
        <p><strong>Chiffon:</strong> Lightweight and flowy. Ideal for summer events and cocktail parties. Easy to drape and comfortable for long wear.</p>
        
        <p><strong>Cotton:</strong> The everyday champion. Breathable, comfortable, and perfect for casual wear or office settings.</p>
        
        <p><strong>Georgette:</strong> Strikes a balance between formal and casual. Holds pleats well and works year-round.</p>
        
        <h2>Colors That Complement</h2>
        <p>Your skin tone plays a role in which colors will make you glow:</p>
        
        <p><strong>Fair skin:</strong> Deep jewel tones like emerald, sapphire, and ruby look stunning. Pastels work beautifully too.</p>
        
        <p><strong>Medium skin:</strong> Lucky you—almost everything works! Earth tones, bright colors, and metallics all complement beautifully.</p>
        
        <p><strong>Deep skin:</strong> Bold, bright colors like fuchsia, orange, and bright yellow are absolutely stunning. White and gold create elegant contrast.</p>
        
        <h2>Occasion Matters</h2>
        <p>Match your saree to the event:</p>
        
        <p><strong>Weddings:</strong> Silk sarees with heavy embroidery or zari work. Think Banarasi, Kanjeevaram, or designer pieces.</p>
        
        <p><strong>Office:</strong> Cotton or linen sarees in subtle colors. Keep embellishments minimal.</p>
        
        <p><strong>Casual outings:</strong> Lightweight fabrics like cotton or chiffon. Fun prints and easy-care materials.</p>
        
        <p><strong>Parties:</strong> Georgette or chiffon with embellishments. Metallic accents and contemporary designs work well.</p>
        
        <h2>Body Type Tips</h2>
        <p>Every body is beautiful, and the right saree enhances your natural shape:</p>
        
        <p><strong>Petite frame:</strong> Avoid heavy fabrics and large prints. Vertical stripes and small prints elongate.</p>
        
        <p><strong>Tall frame:</strong> Bold prints and heavy fabrics look stunning. Wide borders add horizontal interest.</p>
        
        <p><strong>Curvy:</strong> Medium-weight fabrics that drape well. Darker colors and vertical patterns are flattering.</p>
        
        <p>Remember, the most important rule is to wear what makes you feel confident and beautiful. Fashion rules are guidelines, not restrictions!</p>
      `
    },
    'saree-draping-styles': {
      id: 'saree-draping-styles',
      title: 'Saree Draping Styles Across India',
      date: 'January 5, 2026',
      author: 'Meera Reddy',
      category: 'Tutorial',
      image: 'https://images.unsplash.com/photo-1739429944341-db4eefcd230a?w=1200',
      excerpt: 'Explore different regional draping styles and find the one that suits you best.',
      content: `
        <p>India's diversity shines through in the countless ways women drape their sarees. Each style tells a story of regional culture, practical needs, and aesthetic preferences.</p>
        
        <h2>The Classic Nivi Drape</h2>
        <p>The most popular style across India, originating from Andhra Pradesh. The pallu goes over the left shoulder, creating elegant pleats at the waist. Perfect for beginners and works with any saree type.</p>
        
        <p><strong>Best for:</strong> All occasions, all body types<br/>
        <strong>Difficulty:</strong> Easy</p>
        
        <h2>Bengali Style</h2>
        <p>Distinctive for its "key" pleats and the way the pallu is brought over the right shoulder. No pleats at the waist—creating a unique silhouette. Often paired with traditional red-bordered white sarees.</p>
        
        <p><strong>Best for:</strong> Traditional events, pujas<br/>
        <strong>Difficulty:</strong> Moderate</p>
        
        <h2>Gujarati Style</h2>
        <p>The pallu comes from back to front, draping over the right shoulder. Creates a graceful, free-flowing look. Traditionally worn with the pallu covering the head.</p>
        
        <p><strong>Best for:</strong> Weddings, festive occasions<br/>
        <strong>Difficulty:</strong> Moderate</p>
        
        <h2>Maharashtrian Nauvari</h2>
        <p>The nine-yard saree worn without a petticoat. Creates a dhoti-like drape between the legs, allowing freedom of movement. A powerful, distinctive style.</p>
        
        <p><strong>Best for:</strong> Traditional ceremonies<br/>
        <strong>Difficulty:</strong> Advanced</p>
        
        <h2>Butterfly Drape</h2>
        <p>Contemporary style where the pallu is pleated to create a butterfly effect at the shoulder. Modern, stylish, and Instagram-worthy!</p>
        
        <p><strong>Best for:</strong> Parties, modern events<br/>
        <strong>Difficulty:</strong> Moderate</p>
        
        <h2>Lehenga Style</h2>
        <p>Drape the saree like a lehenga—pleats all around instead of just in front. Creates volume and movement, perfect for dancing!</p>
        
        <p><strong>Best for:</strong> Weddings, receptions<br/>
        <strong>Difficulty:</strong> Easy</p>
        
        <h2>Pro Tips for Perfect Draping</h2>
        <p>• Always use good-quality safety pins<br/>
        • Match your petticoat color to your saree<br/>
        • Practice makes perfect—don't get discouraged!<br/>
        • Consider your saree fabric when choosing a draping style<br/>
        • YouTube tutorials are your best friend</p>
        
        <p>At Vastram, we celebrate all draping styles. Each one is beautiful in its own way, just like the women who wear them. Experiment, find what makes you feel most comfortable and confident, and make it your own!</p>
      `
    }
  };

  useEffect(() => {
    if (storyId && stories[storyId]) {
      setStory(stories[storyId]);
      window.scrollTo(0, 0);
    }
  }, [storyId]);

  if (!story) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Story not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="story-detail-page">
      {/* Back Button */}
      <div className="bg-neutral-warm py-4 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate('/drape-stories')}
            className="flex items-center space-x-2 text-forest hover:text-gold transition-colors"
            data-testid="back-to-stories-button"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm uppercase tracking-[0.1em]">Back to Stories</span>
          </button>
        </div>
      </div>

      {/* Hero Image */}
      <div className="relative h-[60vh] overflow-hidden">
        <img
          src={story.image}
          alt={story.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-8 sm:p-12">
          <div className="max-w-4xl mx-auto">
            <span className="inline-block px-4 py-1.5 bg-gold text-forest text-xs uppercase tracking-wider mb-4 font-medium">
              {story.category}
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl text-ivory mb-4 tracking-tight">
              {story.title}
            </h1>
            <div className="flex items-center space-x-4 text-ivory text-sm font-light">
              <span>{story.author}</span>
              <span>•</span>
              <span>{story.date}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <article className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div 
            className="prose prose-lg max-w-none story-content"
            dangerouslySetInnerHTML={{ __html: story.content }}
          />

          {/* Share Section */}
          <div className="mt-16 pt-8 border-t border-gold">
            <p className="text-sm uppercase tracking-[0.2em] text-forest mb-4">Share this story</p>
            <div className="flex space-x-4">
              <a href="#" className="text-forest hover:text-gold transition-colors">Facebook</a>
              <a href="#" className="text-forest hover:text-gold transition-colors">Twitter</a>
              <a href="#" className="text-forest hover:text-gold transition-colors">Pinterest</a>
            </div>
          </div>
        </div>
      </article>

      {/* Related Stories */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-neutral-warm">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-3xl text-forest mb-8 text-center">
            More <em className="italic">Stories</em>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {Object.values(stories)
              .filter(s => s.id !== storyId)
              .slice(0, 2)
              .map((relatedStory) => (
                <Link
                  key={relatedStory.id}
                  to={`/drape-stories/${relatedStory.id}`}
                  className="block group"
                >
                  <div className="aspect-[16/9] overflow-hidden rounded-sm mb-4">
                    <img
                      src={relatedStory.image}
                      alt={relatedStory.title}
                      className="w-full h-full object-cover product-image-hover"
                    />
                  </div>
                  <p className="text-xs uppercase tracking-[0.2em] text-gold mb-2">{relatedStory.date}</p>
                  <h3 className="font-heading text-xl text-forest mb-2 group-hover:text-gold transition-colors">
                    {relatedStory.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">{relatedStory.excerpt}</p>
                </Link>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default StoryDetail;
